import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import ErrorPage from 'pricing-pages-ui/pages/ErrorPage';
import { Redirect, Route, Switch } from 'react-router-dom';
import { COMMERCE } from 'self-service-api/constants/EnrolledProductLines';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { getPrimaryTracker } from 'usage-tracker-container';
import { useIsHubSpotOnCreditsPilot } from './hooks/useIsHubSpotOnCreditsPilot';
import { useShouldHideDataOrSuitePage } from './hooks/useShouldHideDataOrSuitePage';
import AppLayout from './layouts/AppLayout';
import { useAppSelector } from './reducers';
import { HubSpotOnCreditsRoutes } from './routes/HubSpotOnCreditsRoutes';
import { AddOnsPageV1, AddOnsPageV2, BundlePage, CommerceHubPageV1, FreeToolsPageV1, FreeToolsPageV2, HubSpotCreditsPageV1, HubSpotCreditsPageV2, InterstitialPage, MarketingPlusPageV1, MarketingPlusPageV2, MobileRoutes, SmartCrmPageV1, SmartCrmPageV2, SuitePageV1 } from './routes/loadablePages';
import { DefaultInAppRoute, ProductPageRoute, VersionedPageRoute } from './routes/routeHelpers';
import { isAppAuthSelector } from './selectors/AuthSelectors';
import { isUngatedForAddOnsPageV2Selector, isUngatedForHubSpotCreditsPageV2Selector, isUngatedForM2BundlePagesSelector, isUngatedForM3SmartCrmPagesSelector, isUngatedForSeatsSelector } from './selectors/GatingSelectors';
import { ResponsivePageWrapper } from './v2/ResponsivePageWrapper';
import { useIsUngatedForV2PublicHubPages } from './v2/hooks/gating/useIsUngated';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FreeToolsRouteComponent = () => {
  const isUngatedForM2BundlePages = useAppSelector(isUngatedForM2BundlePagesSelector);
  const isUngatedForV2PublicHubPages = useIsUngatedForV2PublicHubPages();
  const isV2Enabled = isUngatedForM2BundlePages || isUngatedForV2PublicHubPages;
  return /*#__PURE__*/_jsx(VersionedPageRoute, {
    V1Component: FreeToolsPageV1,
    V2Component: FreeToolsPageV2,
    isV2Enabled: isV2Enabled,
    navItem: "crm"
  });
};
const MarketingPlusRouteComponent = () => {
  const isUngatedForM2BundlePages = useAppSelector(isUngatedForM2BundlePagesSelector);
  const isUngatedForV2PublicHubPages = useIsUngatedForV2PublicHubPages();
  const isV2Enabled = isUngatedForM2BundlePages || isUngatedForV2PublicHubPages;
  return /*#__PURE__*/_jsx(VersionedPageRoute, {
    V1Component: MarketingPlusPageV1,
    V2Component: MarketingPlusPageV2,
    isV2Enabled: isV2Enabled,
    navItem: "marketing-plus"
  });
};
const SmartCrmRouteComponent = () => {
  const isUngatedForM3SmartCrmPages = useAppSelector(isUngatedForM3SmartCrmPagesSelector);
  const isUngatedForV2PublicHubPages = useIsUngatedForV2PublicHubPages();
  const isV2Enabled = isUngatedForM3SmartCrmPages || isUngatedForV2PublicHubPages;
  return /*#__PURE__*/_jsx(VersionedPageRoute, {
    V1Component: SmartCrmPageV1,
    V2Component: SmartCrmPageV2,
    isV2Enabled: isV2Enabled,
    navItem: "smart-crm"
  });
};
export default function Routes() {
  const {
    isInPilot: isHSoCPilot
  } = useIsHubSpotOnCreditsPilot();
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const shouldHideDataOrSuitePage = useShouldHideDataOrSuitePage();
  const isUngatedForHubSpotCreditsPageV2 = useAppSelector(isUngatedForHubSpotCreditsPageV2Selector);
  const isUngatedForAddOnsPageV2 = useAppSelector(isUngatedForAddOnsPageV2Selector);
  const isMobile = useIsMobile();
  getPrimaryTracker().setProperties({
    isSeatsPilot: isUngatedForSeats
  });
  if (isHSoCPilot) {
    return /*#__PURE__*/_jsx(HubSpotOnCreditsRoutes, {});
  }
  if (isMobile && !isAppAuth) {
    return /*#__PURE__*/_jsx(MobileRoutes, {});
  }
  return /*#__PURE__*/_jsx(AppLayout, {
    children: /*#__PURE__*/_jsxs(Switch, {
      children: [isAppAuth && /*#__PURE__*/_jsx(Route, {
        path: "/:page/*",
        render: ({
          location,
          match: {
            params: {
              page
            }
          }
        }) => /*#__PURE__*/_jsx(Redirect, {
          to: Object.assign({}, location, {
            pathname: `/${page}`
          })
        })
      }), shouldHideDataOrSuitePage && /*#__PURE__*/_jsx(Route, {
        path: ['/data', '/operations', '/suite'],
        children: /*#__PURE__*/_jsx(Redirect, {
          to: "/marketing"
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: "/interstitial",
        component: InterstitialPage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/bundle",
        render: () => /*#__PURE__*/_jsx(ResponsivePageWrapper, {
          navItem: "bundle",
          children: /*#__PURE__*/_jsx(BundlePage, {})
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: "/marketing-plus",
        component: MarketingPlusRouteComponent
      }), /*#__PURE__*/_jsx(Route, {
        path: "/crm",
        component: FreeToolsRouteComponent
      }), /*#__PURE__*/_jsx(Route, {
        path: "/add-ons",
        render: () => /*#__PURE__*/_jsx(VersionedPageRoute, {
          V1Component: AddOnsPageV1,
          V2Component: AddOnsPageV2,
          isV2Enabled: isUngatedForAddOnsPageV2,
          navItem: "add-ons"
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: "/hubspot-credits",
        render: () => /*#__PURE__*/_jsx(VersionedPageRoute, {
          V1Component: HubSpotCreditsPageV1,
          V2Component: HubSpotCreditsPageV2,
          isV2Enabled: isUngatedForHubSpotCreditsPageV2,
          navItem: "hubspot-credits"
        })
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/suite",
        Component: SuitePageV1
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/marketing",
        productLine: MARKETING
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/sales",
        productLine: SALES
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/service",
        productLine: SERVICE
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/content",
        productLine: CMS
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/data",
        productLine: OPERATIONS
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/commerce",
        Component: CommerceHubPageV1,
        productLine: COMMERCE
      }), /*#__PURE__*/_jsx(Route, {
        path: "/smart-crm",
        component: SmartCrmRouteComponent
      }), isAppAuth ? /*#__PURE__*/_jsx(DefaultInAppRoute, {}) : /*#__PURE__*/_jsx(Redirect, {
        from: "/",
        exact: true,
        to: createToObject('/suite')
      }), /*#__PURE__*/_jsx(Route, {
        component: ErrorPage
      })]
    })
  });
}